import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle2, ChevronDown, ChevronRight, ExternalLink, Lock, Pencil, Repeat, Route, Sparkles, Target, Wrench } from 'lucide-react'
import { Button } from '../ui/Button'
import { Timestamp } from '../ui/Timestamp'
import { useToast } from '../ui/Toast'
import { useBranding } from '../../hooks/useBranding'
import { JourneyPreviewModal, loadDraftFromStorage, clearDraftFromStorage, formatDraftAge } from '../journey'
import type { BreakdownStep } from '../../types/journey'
import { DreamService, type Dream, type DreamBoardCard, type DreamBreakdownStep, type DreamGoalItem, type DreamJourneyPlan } from '../../services/dreamService'
import { nextWhenWords } from '../../utils/dreamWords'
import { progressWords } from '../../utils/dreamWords'

/**
 * A dream's journey (Dreams UX pass 2026-09-06, corrected on owner review):
 * the goals under the dream ARE the journey. A dream with one hand-made goal
 * has a journey under way; it never reads "no journey yet". When a plan
 * exists, its steps come first (one goal set up at a time — the goal set up
 * now is "start here", the ones after it are set up when the goal before
 * them is done; the owner has parked a tightening of that rule) and the
 * goals outside the plan follow as "Also under it". Without a plan the goals
 * are listed as the journey so far and the goal to work on is marked.
 * "Plan the journey" (nothing under it yet) or "Propose the next goals"
 * (goals already there — the engine is told about them and proposes gaps)
 * asks the persona once and opens the review; "Write my own goals" opens it
 * empty; "Edit journey" for what is not done yet.
 */
const DRAFT_KEY = 'dream_journey_draft'

interface Props {
  dream: Dream
  card: DreamBoardCard | null
  live: boolean
  headingClass: string
  tz?: string | null
  onPlan: (plan: DreamJourneyPlan | null) => void
  refreshKey?: string | number | null
  autoStart?: boolean
  /** True when the page has no other next move: "Plan the journey" is then its one filled button (rule 3). */
  leads?: boolean
}

const toReview = (s: { step_id?: string; title: string; description?: string | null; estimated_duration_minutes?: number | null; order?: number | null }, i: number): BreakdownStep =>
  ({ step_id: s.step_id, title: s.title, description: s.description || '', estimated_duration_minutes: Number(s.estimated_duration_minutes || 0), order: s.order || i + 1, child_type: 'goal' })
const toApi = (steps: BreakdownStep[]): DreamBreakdownStep[] =>
  steps.map((s, i) => ({ step_id: s.step_id, title: s.title, description: s.description || '', estimated_duration_minutes: s.estimated_duration_minutes, order: i + 1, child_type: 'goal' }))

export function DreamJourney({ dream, card, live, headingClass, tz, onPlan, refreshKey, autoStart = false, leads = false }: Props) {
  const toast = useToast()
  const { assistant: personaName } = useBranding()
  const svc = useRef(new DreamService()).current
  const dreamId = Number(dream.id)
  const [plan, setPlan] = useState<DreamJourneyPlan | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [open, setOpen] = useState(true)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [preview, setPreview] = useState<BreakdownStep[] | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalLoading, setModalLoading] = useState(false)
  const [editing, setEditing] = useState(false)
  const [completedCount, setCompletedCount] = useState(0)
  const [accepting, setAccepting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [draftAge, setDraftAge] = useState<string | null>(null)
  const [resumed, setResumed] = useState(false)
  const autoStarted = useRef(false)

  const load = useCallback(async () => {
    try {
      const p = await svc.getJourneyPlan(dreamId)
      setPlan(p); onPlan(p)
    } catch { setPlan(null); onPlan(null) }
    finally { setLoaded(true) }
  }, [svc, dreamId, onPlan])
  useEffect(() => { void load() }, [load, refreshKey])
  useEffect(() => {
    const d = loadDraftFromStorage(DRAFT_KEY, dreamId)
    setDraftAge(d ? formatDraftAge(d.savedAt) : null)
  }, [dreamId, modalOpen])

  const openPreview = (steps: BreakdownStep[] | null, opts: { loading?: boolean; resumed?: boolean } = {}) => {
    setPreview(steps); setEditing(false); setCompletedCount(0); setResumed(Boolean(opts.resumed)); setModalLoading(Boolean(opts.loading)); setModalOpen(true)
  }

  const planJourney = async () => {
    setErr(null); setBusy(true)
    openPreview(null, { loading: true })
    try {
      const r = await svc.previewBreakdown(dreamId)
      setPreview(r.steps.map(toReview))
    } catch (e) {
      setModalOpen(false)
      setErr(e instanceof Error ? e.message : 'Could not plan the journey just now')
    } finally { setBusy(false); setModalLoading(false) }
  }

  useEffect(() => {
    if (!autoStart || autoStarted.current || !loaded || plan) return
    autoStarted.current = true
    void planJourney()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, loaded, plan])

  const resumeDraft = () => { const d = loadDraftFromStorage(DRAFT_KEY, dreamId); if (d) openPreview(d.steps, { resumed: true }) }
  const discardDraft = () => { clearDraftFromStorage(DRAFT_KEY, dreamId); setDraftAge(null); setResumed(false); setModalOpen(false); setPreview(null) }

  const accept = async (steps: BreakdownStep[]) => {
    setAccepting(true)
    try {
      const r = await svc.acceptBreakdown(dreamId, toApi(steps))
      clearDraftFromStorage(DRAFT_KEY, dreamId); setDraftAge(null)
      setModalOpen(false); setPreview(null)
      await load()
      const n = (r.steps || []).length
      toast.toast({ variant: 'success', title: 'Journey started', description: r.created_entity ? `${n} goal${n === 1 ? '' : 's'} in the journey. The first, "${r.created_entity.title}", is set up under this dream — start there.` : 'The first goal is on its way.' })
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not start the journey')
    } finally { setAccepting(false) }
  }

  const edit = () => {
    if (!plan) return
    setPreview(plan.steps.map(toReview))
    setCompletedCount(plan.steps.filter((s) => s.status === 'completed').length)
    setEditing(true); setResumed(false); setModalLoading(false); setModalOpen(true)
  }

  const saveEdit = async (steps: BreakdownStep[]) => {
    setSaving(true)
    try {
      await svc.updateJourneyPlan(dreamId, toApi(steps))
      setModalOpen(false); setPreview(null); setEditing(false)
      await load()
      toast.success('Journey updated')
    } catch (e) {
      toast.error('Could not save the journey', e instanceof Error ? e.message : 'Please try again.')
    } finally { setSaving(false) }
  }

  const steps = plan?.steps || []
  const done = steps.filter((s) => s.status === 'completed').length
  const startHereId = steps.find((s) => s.status === 'unlocked' && s.entity_id != null)?.step_id || steps.find((s) => s.status === 'unlocked')?.step_id || null
  const inJourney = new Set(steps.map((s) => s.entity_id).filter((x): x is number => x != null))
  const goalById = new Map((card?.goals || []).map((g) => [g.id, g]))
  const goals = card?.goals || []
  // without a plan, the goals under the dream are the journey so far; with one, the ones outside it follow the steps
  const soFar = plan ? [] : goals
  const others = plan ? goals.filter((g) => !inJourney.has(g.id)) : []
  const growing = !plan && soFar.length > 0
  const workOnId = card?.next?.via?.id ?? (card?.next?.kind === 'goal' ? card.next.id : null) ?? soFar.find((g) => g.live)?.id ?? null

  const goalLine = (g: DreamGoalItem) => {
    const c = g.card
    const when = nextWhenWords(c?.next ? { ...c.next, via: null } : null, tz)
    const s = String(g.status || '').toLowerCase()
    if (s === 'completed') return <>Done{g.completed_at ? <> <Timestamp value={g.completed_at} mode="date" /></> : null}</>
    if (!g.live) return <>Paused</>
    return (
      <>
        {c?.today ? <>Something done today{c.today.at ? <> <Timestamp value={c.today.at} mode="time" /></> : null}</> : c?.next ? <>next: {c.next.title}{when ? ` · ${when}` : ''}</> : c?.path ? `journey ${c.path.status === 'completed' ? 'complete' : `${c.path.done} of ${c.path.total}`}` : 'Nothing next yet · give it a journey or a next step'}
        {progressWords(g.progress) ? <> · {progressWords(g.progress)}</> : null}
      </>
    )
  }
  const NextIcon = ({ kind, className }: { kind?: string | null; className: string }) => (kind === 'habit' ? <Repeat className={className} /> : kind === 'skill' ? <Wrench className={className} /> : <Target className={className} />)

  return (
    <section data-testid="dream-journey">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="flex items-center gap-1.5 text-left">
          {open ? <ChevronDown className="h-3.5 w-3.5 text-muted" /> : <ChevronRight className="h-3.5 w-3.5 text-muted" />}
          <h3 className={headingClass}><Route className="h-3.5 w-3.5 text-brand" /> Journey</h3>
          {plan && <span className="text-app-micro text-muted" data-testid="dream-journey-count">{plan.status === 'completed' ? 'complete' : `${done} of ${steps.length}`}</span>}
          {growing && <span className="text-app-micro text-muted" data-testid="dream-journey-count">{soFar.length} goal{soFar.length === 1 ? '' : 's'} so far</span>}
          {others.length > 0 && <span className="text-app-micro text-muted" data-testid="dream-journey-tracked">· {others.length} more goal{others.length === 1 ? '' : 's'}</span>}
        </button>
        {plan && plan.status !== 'completed' && live && (
          <button type="button" onClick={edit} className="ml-auto inline-flex items-center gap-1 text-app-micro font-medium text-brand hover:underline" data-testid="dream-journey-edit"><Pencil className="h-3 w-3" /> Edit journey</button>
        )}
      </div>

      {open && plan && (
        <ol className="mt-2 space-y-1.5" data-testid="dream-journey-steps">
          {steps.map((step, idx) => {
            const isDone = step.status === 'completed'
            const locked = step.status === 'locked'
            const startHere = step.step_id === startHereId
            const eid = step.entity_id ?? null
            const goal = eid != null ? goalById.get(eid) : undefined
            const href = eid != null ? `/goals/${eid}` : null
            return (
              <li key={step.step_id} className={`flex items-start gap-2 rounded-md text-sm ${startHere ? 'bg-brand-subtle/60 -mx-2 px-2 py-1.5' : ''}`} data-testid="dream-journey-step" data-start-here={startHere || undefined} data-locked={locked || undefined}>
                <span className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-app-micro font-bold ${isDone ? 'bg-ok text-on-brand' : startHere ? 'bg-brand text-on-brand ring-2 ring-brand/30' : 'bg-surface-alt text-muted'}`}>
                  {isDone ? <CheckCircle2 className="h-3 w-3" /> : locked ? <Lock className="h-3 w-3" /> : idx + 1}
                </span>
                <span className="min-w-0">
                  <span className={isDone || !locked ? 'text-primary' : 'text-muted'}>{step.title}</span>
                  <span className="ml-2 rounded bg-surface-alt px-1 py-0.5 text-app-nano font-medium uppercase tracking-wider text-muted" data-testid="dream-journey-kind">goal</span>
                  {startHere && <span className="ml-2 text-app-micro font-medium text-brand" data-testid="dream-journey-start-here">start here</span>}
                  {href && <Link to={href} className="ml-2 inline-flex items-center gap-1 text-app-micro text-brand hover:underline" data-testid="dream-journey-open"><ExternalLink className="h-3 w-3" /> open</Link>}
                  {isDone && step.completed_at && <span className="ml-2 text-app-micro text-muted">done <Timestamp value={step.completed_at} mode="date" /></span>}
                  {!isDone && !locked && goal && <span className="block text-app-micro text-secondary">{goalLine(goal)}</span>}
                  {!isDone && !locked && !goal && eid == null && <span className="block text-app-micro text-secondary">Being set up</span>}
                  {locked && <span className="block text-app-micro text-muted">Set up when the goal before it is done</span>}
                </span>
              </li>
            )
          })}
        </ol>
      )}

      {open && growing && (
        <ol className="mt-2 space-y-1.5" data-testid="dream-journey-goals">
          {soFar.map((g, idx) => {
            const s = String(g.status || '').toLowerCase()
            const doneGoal = s === 'completed'
            const workOn = g.id === workOnId && g.live
            return (
              <li key={g.id} className={`flex items-start gap-2 rounded-md text-sm ${workOn ? 'bg-brand-subtle/60 -mx-2 px-2 py-1.5' : ''}`} data-testid="dream-journey-goal" data-work-on={workOn || undefined} data-live={g.live}>
                <span className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-app-micro font-bold ${doneGoal ? 'bg-ok text-on-brand' : workOn ? 'bg-brand text-on-brand ring-2 ring-brand/30' : g.live ? 'bg-surface-alt text-secondary' : 'bg-surface-alt text-muted'}`}>
                  {doneGoal ? <CheckCircle2 className="h-3 w-3" /> : idx + 1}
                </span>
                <span className="min-w-0">
                  <Link to={`/goals/${g.id}`} className={`hover:text-brand hover:underline ${g.live || doneGoal ? 'text-primary' : 'text-muted'}`}>{g.title}</Link>
                  <span className="ml-2 rounded bg-surface-alt px-1 py-0.5 text-app-nano font-medium uppercase tracking-wider text-muted" data-testid="dream-journey-kind">goal</span>
                  {workOn && <span className="ml-2 text-app-micro font-medium text-brand" data-testid="dream-journey-work-on">work on this</span>}
                  <span className="block text-app-micro text-secondary">{goalLine(g)}</span>
                </span>
              </li>
            )
          })}
        </ol>
      )}

      {open && loaded && growing && live && (
        <div className="mt-3 space-y-2" data-testid="dream-journey-grow">
          <p className="text-app-meta text-secondary">{soFar.some((g) => g.live) ? `The journey is under way with what is above. When you want, ${personaName} can look at how it is going and propose the next goal or two — you decide which, if any, to take on.` : `Nothing under it is moving right now. ${personaName} can propose the next goal or two — you decide which, if any, to take on.`}</p>
          {draftAge && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-brand/20 bg-brand-subtle p-2 text-app-meta">
              <span className="text-primary">You have unsaved proposed goals, saved {draftAge}.</span>
              <Button size="xs" onClick={resumeDraft} data-testid="dream-journey-resume">Resume them</Button>
              <button type="button" onClick={discardDraft} className="text-app-micro text-muted underline decoration-dotted hover:text-secondary">Discard</button>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button size="xs" variant="outline" className="hover:bg-brand-subtle" disabled={busy} onClick={() => void planJourney()} data-testid="dream-journey-plan"><Sparkles className="mr-1.5 h-3.5 w-3.5" />Propose the next goals</Button>
            <button type="button" onClick={() => openPreview([])} className="text-app-micro font-medium text-brand hover:underline" data-testid="dream-journey-own">Add my own goals</button>
          </div>
          {busy && <p className="text-app-micro text-muted">Working on it…</p>}
        </div>
      )}

      {open && loaded && !plan && !growing && (
        <div className="mt-2 space-y-2" data-testid="dream-journey-empty">
          <p className="text-app-meta text-secondary">No journey yet. {live ? `${personaName} can plan one — a few goals in order, each set up when the one before it is done — you review it, change what you like, then start. Or write the goals yourself.` : 'It can be planned once this is picked back up.'}</p>
          {live && draftAge && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-brand/20 bg-brand-subtle p-2 text-app-meta">
              <span className="text-primary">You have an unsaved journey, saved {draftAge}.</span>
              <Button size="xs" onClick={resumeDraft} data-testid="dream-journey-resume">Resume it</Button>
              <button type="button" onClick={discardDraft} className="text-app-micro text-muted underline decoration-dotted hover:text-secondary">Discard</button>
            </div>
          )}
          {live && (
            <div className="flex flex-wrap items-center gap-2">
              <Button size="xs" variant={leads && !draftAge ? 'default' : 'outline'} className={leads && !draftAge ? '' : 'hover:bg-brand-subtle'} disabled={busy} onClick={() => void planJourney()} data-testid="dream-journey-plan"><Sparkles className="mr-1.5 h-3.5 w-3.5" />Plan the journey</Button>
              <button type="button" onClick={() => openPreview([])} className="text-app-micro font-medium text-brand hover:underline" data-testid="dream-journey-own">Write my own goals</button>
            </div>
          )}
          {busy && <p className="text-app-micro text-muted">Working on it…</p>}
        </div>
      )}

      {open && others.length > 0 && (
        <div className="mt-3" data-testid="dream-journey-items">
          <p className="text-app-micro font-medium uppercase tracking-widest text-muted">{plan ? 'Also under it' : 'Under it'}</p>
          <ul className="mt-1.5 space-y-1.5">
            {others.map((g) => {
              const s = String(g.status || '').toLowerCase()
              const doneGoal = s === 'completed'
              return (
                <li key={g.id} className="flex items-start gap-2 text-sm" data-testid="dream-item-goal" data-live={g.live}>
                  <span className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${doneGoal ? 'bg-ok text-on-brand' : g.card?.today ? 'bg-ok/20 text-ok' : 'bg-surface-alt text-secondary'}`} aria-hidden="true">
                    {doneGoal ? <CheckCircle2 className="h-3 w-3" /> : <NextIcon kind={g.card?.next?.kind} className="h-3 w-3" />}
                  </span>
                  <span className="min-w-0">
                    <Link to={`/goals/${g.id}`} className={`hover:text-brand hover:underline ${g.live ? 'text-primary' : 'text-muted'}`}>{g.title}</Link>
                    <span className="ml-2 text-app-micro text-muted">goal</span>
                    <span className="block text-app-micro text-secondary">{goalLine(g)}</span>
                  </span>
                </li>
              )
            })}
          </ul>
        </div>
      )}
      {err && <p className="mt-2 text-xs text-bad" role="alert">{err}</p>}

      <JourneyPreviewModal
        open={modalOpen}
        onOpenChange={(o) => { if (!o) { setModalOpen(false); setPreview(null); setEditing(false) } }}
        title={editing ? 'Edit the journey' : growing ? `Next goals for ${dream.title}` : `The journey for ${dream.title}`}
        description={editing ? 'Change what is not done yet. Finished goals stay where they are.' : growing ? 'Proposed to continue what is already under this dream. Keep the ones that read like yours, in the order you would take them. Then start: the first is set up under this dream now, and each next one when the goal before it is done.' : 'Change the goals until they read like yours, in the order you would take them. Then start: the first goal is set up under this dream now, and each next one when the goal before it is done.'}
        steps={preview}
        loading={modalLoading}
        onStepsChange={setPreview}
        onAccept={accept}
        onSaveEdit={saveEdit}
        isEditingActive={editing}
        completedCount={completedCount}
        accepting={accepting}
        savingEdit={saving}
        draftKey={DRAFT_KEY}
        entityId={dreamId}
        entityTitle={dream.title}
        resumedFromDraft={resumed}
        onDiscardDraft={discardDraft}
        showStepTypeSelector={false}
      />
    </section>
  )
}

export default DreamJourney
